import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { MILU_PAID_ADDON_PENDING_EVENT, hasPendingMiluPaidAddon } from '@theme/milu-gift';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #pendingAdds = 0;
  #shippingTotal = null;
  #shippingPendingTotal = null;
  #shippingEventId = 0;
  #shippingAppliedId = 0;
  #shippingLifecycle = 0;
  #shippingObserver = null;
  #shippingFrame = 0;
  #shippingWaitTimer = 0;
  #shippingAnimationTimer = 0;
  #shippingAnimatedBar = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#shippingLifecycle++;
    const shippingBar = this.querySelector('[data-milu-shipping-total]');
    // Seed from server-rendered state: opening/reloading an eligible cart is not a new reward.
    this.#shippingTotal = shippingBar
      ? Number(shippingBar.dataset.miluShippingTotal)
      : this.querySelector('[data-cart-drawer-empty]') ? 0 : null;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleShippingUpdate);
    document.addEventListener(MILU_PAID_ADDON_PENDING_EVENT, this.#syncBusyState);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);
    this.#syncBusyState();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#shippingLifecycle++;
    this.#clearShippingCelebration();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleShippingUpdate);
    document.removeEventListener(MILU_PAID_ADDON_PENDING_EVENT, this.#syncBusyState);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    // A restored cart is a new baseline, never a reason to replay an old celebration.
    this.#clearShippingCelebration();
    const shippingBar = this.querySelector('[data-milu-shipping-total]');
    this.#shippingTotal = shippingBar
      ? Number(shippingBar.dataset.miluShippingTotal)
      : this.querySelector('[data-cart-drawer-empty]') ? 0 : null;
    this.#syncBusyState();
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Keep preflight and native cart-event locks independent so either can finish first. */
  #syncBusyState = () => {
    const busy = this.#pendingAdds > 0 || hasPendingMiluPaidAddon();
    if (busy) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    const items = this.querySelector('cart-items-component');
    if (items) items.inert = busy;
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#queueShippingCelebration();
  };

  /** Track settled totals only; this enhancement never fetches or delays a cart mutation. */
  #handleShippingUpdate = (event) => {
    if (!event.promise) return;
    const eventId = ++this.#shippingEventId;
    const lifecycle = this.#shippingLifecycle;
    event.promise.then(({ cart, detail }) => {
      if (!this.isConnected || lifecycle !== this.#shippingLifecycle || eventId < this.#shippingAppliedId) return;
      const cost = cart?.cost?.totalAmount;
      const total = Math.round(Number(cost?.amount) * 100);
      if (cost?.currencyCode !== 'USD' || !Number.isFinite(total)) return;
      this.#shippingAppliedId = eventId;
      const crossed = this.#shippingTotal !== null && this.#shippingTotal < 4900 && total >= 4900;
      this.#shippingTotal = total;
      if (detail?.didError || total < 4900 || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.#clearShippingCelebration();
        return;
      }
      if (!crossed && this.#shippingPendingTotal === null) return;
      this.#shippingPendingTotal = total;
      // Section rendering is asynchronous. Observe only until the matching total arrives.
      if (!this.#shippingObserver) {
        this.#shippingObserver = new MutationObserver(this.#queueShippingCelebration);
        this.#shippingObserver.observe(this, {
          childList: true, subtree: true, attributes: true, attributeFilter: ['data-milu-shipping-total'],
        });
        this.#shippingWaitTimer = setTimeout(() => this.#clearShippingCelebration(), 5000);
      }
      this.#queueShippingCelebration();
    }).catch(() => { /* Cart owners report request failures; no cosmetic failure may block checkout. */ });
  };

  #queueShippingCelebration = () => {
    if (this.#shippingPendingTotal === null || this.#shippingFrame) return;
    this.#shippingFrame = requestAnimationFrame(() => {
      this.#shippingFrame = 0;
      const bar = this.querySelector('[data-milu-shipping-total]');
      if (!bar || Number(bar.dataset.miluShippingTotal) !== this.#shippingPendingTotal) return;
      const visible = this.#themeDrawer?.isOpen || this.#themeDrawer?.hasAttribute('open');
      this.#clearShippingCelebration();
      if (!visible || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      bar.classList.add('is-celebrating');
      this.#shippingAnimatedBar = bar;
      this.#shippingAnimationTimer = setTimeout(() => {
        bar.classList.remove('is-celebrating');
        this.#shippingAnimatedBar = null;
        this.#shippingAnimationTimer = 0;
      }, 800);
    });
  };

  #clearShippingCelebration() {
    this.#shippingPendingTotal = null;
    this.#shippingObserver?.disconnect();
    this.#shippingObserver = null;
    cancelAnimationFrame(this.#shippingFrame);
    this.#shippingFrame = 0;
    clearTimeout(this.#shippingWaitTimer);
    clearTimeout(this.#shippingAnimationTimer);
    this.#shippingWaitTimer = this.#shippingAnimationTimer = 0;
    this.#shippingAnimatedBar?.classList.remove('is-celebrating');
    this.#shippingAnimatedBar = null;
  }

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    this.#handleShippingUpdate(event);
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Give feedback immediately, including on the first add to an empty cart.
    const showLoading = (event.action === 'add' || event.context === 'product') && !sourceModal && event.promise;
    if (showLoading) {
      this.#pendingAdds++;
      this.#syncBusyState();
      const finish = () => {
        this.#pendingAdds--;
        requestAnimationFrame(() => this.#syncBusyState());
      };
      event.promise.then(({ detail }) => {
        finish();
        if (detail?.didError && shouldAutoOpen) this.#themeDrawer?.close();
      }, () => {
        finish();
        if (shouldAutoOpen) this.#themeDrawer?.close();
      });
    }

    if (shouldAutoOpen && !sourceModal) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
